import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import _cloneDeep from 'lodash/cloneDeep';
import { getAppName } from 'src/utils/app';
import { ADVERTISING_DISPLAY_METRICS_FIELDS } from 'src/utils/entityDefinitions/fields/beaconAdvertisingFieldDefinitions';

export enum ExportDropdownOptionIds {
  PRODUCT = 'stacklineSku',
  BRAND = 'brandId',
  CATEGORY = 'categoryId',
  SUBCATEGORY = 'subCategoryId',
  BRAND_AND_SUBCATEGORY = 'brandId,subCategoryId',
  KEYWORD = 'searchTerm',
  BRAND_AND_SEARCH_TERM = 'brandId,searchTerm',
  PRODUCT_AND_KEYWORD = 'stacklineSku,searchTerm',
  CAMPAIGN_TYPE = 'campaignType',
  CAMPAIGN = 'campaignId',
  PRODUCT_AND_SELLER = 'stacklineSku,merchantId',
  BRAND_AND_SELLER = 'brandId,merchantId',
  CATEGORY_AND_SELLER = 'categoryId,merchantId',
  SUB_CATEGORY_AND_SELLER = 'subCategoryId,merchantId',
  BRAND_SUBCATEGORY_AND_SELLER = 'brandId,subCategoryId,merchantId',
  REVIEW = 'reviews'
}

const fieldToArrayFormatter = (indexName, fieldName) => {
  const parsedField = [];
  const appName = getAppName();
  const indexField = _cloneDeep(INDEX_FIELDS.getField(appName, indexName, fieldName));

  const {
    displayName,
    name,
    aggregationFunction,
    aggregationFunctionType,
    canBeExported,
    aggregationFunctionExpression
  } = indexField;

  parsedField.push(displayName);
  parsedField.push(name);

  if (aggregationFunctionType) {
    parsedField.push(aggregationFunctionType);
  } else {
    parsedField.push(aggregationFunction);
  }

  if (canBeExported !== undefined) {
    parsedField.push(canBeExported);
  } else {
    parsedField.push(true);
  }

  if (aggregationFunctionType) {
    parsedField.push(aggregationFunctionExpression);
  }

  return parsedField;
};

const aggregationListGenerator = (indexName, fieldNames) => {
  const aggregationList = [];

  fieldNames.forEach((fieldName) => {
    const fieldArray = fieldToArrayFormatter(indexName, fieldName);
    aggregationList.push(fieldArray);
  });

  return aggregationList;
};

export const atlasV2ExportAggregationFieldsMatcher = () => ({
  scorecard: {
    all: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      period: 'year'
    }
  },
  trafficV2: {
    totalClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'otherClicks',
        'totalClicks',
        'adSpend'
      ]),
      sortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
      period: 'year'
    },
    organicClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'otherClicks',
        'totalClicks',
        'adSpend'
      ]),
      sortByField: fieldToArrayFormatter('traffic', 'organicClicks'),
      period: 'year'
    },
    otherClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'otherClicks',
        'totalClicks',
        'adSpend'
      ]),
      sortByField: fieldToArrayFormatter('traffic', 'otherClicks'),
      period: 'year'
    },
    adClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'otherClicks',
        'totalClicks',
        'adSpend'
      ]),
      sortByField: fieldToArrayFormatter('traffic', 'adClicks'),
      period: 'year'
    }
  },
  traffic: {
    totalClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', ['organicClicks', 'adClicks', 'totalClicks', 'adSpend']),
      sortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
      period: 'year'
    },
    organicClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', ['organicClicks', 'adClicks', 'totalClicks', 'adSpend']),
      sortByField: fieldToArrayFormatter('traffic', 'organicClicks'),
      period: 'year'
    },
    adClicks: {
      indexName: 'atlas-traffic',
      aggregationList: aggregationListGenerator('traffic', ['organicClicks', 'adClicks', 'totalClicks', 'adSpend']),
      sortByField: fieldToArrayFormatter('traffic', 'adClicks'),
      period: 'year'
    }
  },
  conversion: {
    purchaseRate: {
      shouldExportMultipleRequest: ['atlas-sales', 'atlas-traffic'],
      'atlas-sales': {
        indexName: 'atlas-sales',
        aggregationList: aggregationListGenerator('sales', ['unitsSold', 'retailSales', 'retailPrice']),
        derivedFieldsJson:
          '[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"atlas","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"atlas","indexName":"sales"}]',
        sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
        period: 'year'
      },
      'atlas-traffic': {
        indexName: 'atlas-traffic',
        aggregationList: aggregationListGenerator('traffic', ['totalClicks', 'organicClicks']),
        sortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
        period: 'year',
        derivedFieldsJson:
          '[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"atlas","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"atlas","indexName":"sales"}]'
      }
    }
  },
  prices: {
    retailPrice: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
      period: 'year'
    },
    promotions: {
      indexName: 'atlas-promotions',
      aggregationList: aggregationListGenerator('promotions', ['retailSales']),
      aggregationSortByField: fieldToArrayFormatter('promotions', 'retailSales'),
      shouldUserNullAggregation: true,
      searchBy: 'child',
      shouldOverrideConditionFilter: true,
      isReturnDocument: true,
      period: 'year',
      exportExtraRangeFilterConditions: [{ fieldName: 'retailSales', minValue: 0.01 }]
    }
  },
  sales: {
    retailSales: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      period: 'year'
    },
    unitsSold: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
      period: 'year'
    }
  },
  content: {
    contentScore: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', [
        'contentScore',
        'titleScore',
        'bulletScore',
        'imageScore',
        'videoScore',
        'aplusScore',
        'retailSales'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'contentScore')
    }
  },
  rating: {
    rating: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'reviewsRating', 'reviewsCount']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      exportExtraRangeFilterConditions: [{ fieldName: 'reviewsRating', minValue: 0.01, maxValue: 5 }]
    }
  },
  // soon to be deprecated after doris migration - temp settings for v2 tabs
  scorecardV2: {
    all: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      period: 'year'
    }
  },
  conversionV2: {
    purchaseRate: {
      shouldExportMultipleRequest: ['atlas-sales', 'atlas-traffic'],
      'atlas-sales': {
        indexName: 'atlas-sales',
        aggregationList: aggregationListGenerator('sales', ['unitsSold', 'retailSales', 'retailPrice']),
        derivedFieldsJson:
          '[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"atlas","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"atlas","indexName":"sales"}]',
        sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
        period: 'year'
      },
      'atlas-traffic': {
        indexName: 'atlas-traffic',
        aggregationList: aggregationListGenerator('traffic', ['totalClicks', 'organicClicks']),
        sortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
        period: 'year',
        derivedFieldsJson:
          '[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"atlas","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"atlas","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"atlas","indexName":"sales"}]'
      }
    }
  },
  salesV2: {
    retailSales: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      period: 'year'
    },
    unitsSold: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
      period: 'year'
    }
  },
  pricesV2: {
    retailPrice: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
      sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
      period: 'year'
    },
    promotions: {
      indexName: 'atlas-promotions',
      aggregationList: aggregationListGenerator('promotions', ['retailSales']),
      aggregationSortByField: fieldToArrayFormatter('promotions', 'retailSales'),
      shouldUserNullAggregation: true,
      searchBy: 'child',
      shouldOverrideConditionFilter: true,
      isReturnDocument: true,
      period: 'year',
      exportExtraRangeFilterConditions: [{ fieldName: 'retailSales', minValue: 0.01 }]
    }
  },
  contentV2: {
    contentScore: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', [
        'contentScore',
        'titleScore',
        'bulletScore',
        'imageScore',
        'videoScore',
        'aplusScore',
        'retailSales'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'contentScore')
    }
  },
  ratingV2: {
    rating: {
      indexName: 'atlas-sales',
      aggregationList: aggregationListGenerator('sales', ['retailSales', 'reviewsRating', 'reviewsCount']),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      exportExtraRangeFilterConditions: [{ fieldName: 'reviewsRating', minValue: 0.01, maxValue: 5 }]
    }
  },
  default: {
    indexName: 'atlas-sales',
    aggregationList: aggregationListGenerator('sales', ['retailSales', 'unitsSold', 'retailPrice']),
    sortByField: fieldToArrayFormatter('sales', 'retailSales'),
    period: 'year'
  }
});

export const exportAggregationFieldsMatcher = () => ({
  default: {
    indexName: 'beacon-sales',
    aggregationList: [['Retail Sales', 'retailSales', 'sum', true]],
    sortByField: fieldToArrayFormatter('sales', 'retailSales')
  },

  sales: {
    keymetrics: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'wholesaleSales',
        'retailPrice',
        'inStockWeighted',
        'inStock',
        'instockRate',
        'unitsOnHand'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'retailSales')
    },
    retailSales: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'wholesaleSales',
        'retailPrice',
        'inStockWeighted',
        'inStock',
        'instockRate',
        'unitsOnHand'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'retailSales')
    },
    unitsSold: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'wholesaleSales',
        'retailPrice',
        'inStockWeighted',
        'inStock',
        'instockRate',
        'unitsOnHand'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'retailSales')
    },
    retailerGrossMargin: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'wholesaleSales',
        'retailPrice',
        'retailerGrossMargin',
        'brandCogs',
        'brandGrossMargin'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'retailerGrossMargin')
    },
    returnRate: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'wholesaleSales',
        'retailPrice',
        'unitsReturned',
        'returnRate'
      ]),
      aggregationSortByField: fieldToArrayFormatter('sales', 'unitsReturned'),
      sortByField: fieldToArrayFormatter('sales', 'retailSales')
    },
    retailPrice: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'retailPrice',
        'mapPrice',
        'wholesaleSales',
        'wholesalePrice',
        'retailerGrossMargin',
        'retailerMarginPerUnit',
        'brandCogs',
        'brandCogsPerUnit',
        'brandGrossMargin',
        'brandGrossMarginPerUnit'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'retailPrice')
    },
    mapPrice: {
      indexName: 'beacon-sales',
      aggregationList: aggregationListGenerator('sales', [
        'retailSales',
        'unitsSold',
        'retailPrice',
        'mapPrice',
        'mapPriceViolation'
      ]),
      sortByField: fieldToArrayFormatter('sales', 'mapPriceViolation')
    },
    inventory: {
      indexName: 'beacon-sales',
      aggregationList: [
        fieldToArrayFormatter('sales', 'retailSales'),
        fieldToArrayFormatter('sales', 'unitsSold'),
        fieldToArrayFormatter('sales', 'retailPrice'),
        fieldToArrayFormatter('sales', 'inStockWeighted'),
        fieldToArrayFormatter('sales', 'inStock'),
        [
          'In-Stock Rate',
          'instockRate',
          'computed',
          true,
          '((unitsSold_sum > 0) ? ((inStockWeighted_sum / unitsSold_sum)) : (inStock_sum))'
        ],
        fieldToArrayFormatter('sales', 'unitsOnHand'),

        fieldToArrayFormatter('sales', 'inventoryRetailValue')
      ],
      aggregationSortByField: fieldToArrayFormatter('sales', 'unitsOnHand'),
      sortByField: fieldToArrayFormatter('sales', 'retailSales'),
      period: 'year'
    },
    promotions: {
      indexName: 'beacon-promotions',
      aggregationList: [['Retail Price', 'retailPrice', 'avg', true]]
    },
    growthmatrix: {
      shouldExportMultipleRequest: ['beacon-productGrowthMetrics-product', 'beacon-adjustments'],
      'beacon-productGrowthMetrics-product': {
        indexName: 'beacon-productGrowthMetrics-product',
        aggregationList: [
          ['Retail Sales', 'retailSales', 'sum', true],
          ['Total Traffic', 'beaconTotalTraffic', 'sum', true],
          ['Traffic Index', 'trafficIndex', 'avg', true],
          ['Conversion Rate', 'beaconConversion', 'avg', true],
          ['Conversion Index', 'conversionIndex', 'avg', true]
        ],
        searchType: 'parent',
        sortByField: fieldToArrayFormatter('sales', 'retailSales'),
        isReturnDocument: false
      },
      'beacon-adjustments': {
        indexName: 'beacon-adjustments',
        aggregationList: [['Adjustment Count', 'planType', 'cardinality', true]],
        searchType: 'parent',
        isReturnDocument: false
      }
    }
  },
  traffic: {
    totalClicks: {
      indexName: 'beacon-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'totalClicks',
        'adSpend',
        'adRetailSales'
      ]),
      aggregationSortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
      sortByField: fieldToArrayFormatter('traffic', 'organicClicks'),
      period: 'year'
    },
    organicClicks: {
      indexName: 'beacon-traffic',
      aggregationList: aggregationListGenerator('traffic', [
        'organicClicks',
        'adClicks',
        'otherClicks',
        'totalClicks',
        'adSpend',
        'adRetailSales'
      ]),
      sortByField: fieldToArrayFormatter('traffic', 'organicClicks')
    },
    adClicks: {
      indexName: 'beacon-advertising',
      aggregationList: aggregationListGenerator('advertising', [
        'clicks',
        'spend',
        'sales',
        'unitsSold',
        'returnOnAdSpend',
        'campaignId'
      ]),
      sortByField: fieldToArrayFormatter('advertising', 'clicks')
    },
    dpvViews: {
      indexName: 'beacon-advertisingDisplay',
      aggregationList: aggregationListGenerator('advertisingDisplay', [
        ADVERTISING_DISPLAY_METRICS_FIELDS.impressions.name,
        ADVERTISING_DISPLAY_METRICS_FIELDS.dpv.name,
        ADVERTISING_DISPLAY_METRICS_FIELDS.spend.name,
        ADVERTISING_DISPLAY_METRICS_FIELDS.sales.name,
        ADVERTISING_DISPLAY_METRICS_FIELDS.unitsSold.name,
        ADVERTISING_DISPLAY_METRICS_FIELDS.returnOnAdSpend.name
      ]),
      sortByField: fieldToArrayFormatter('advertisingDisplay', ADVERTISING_DISPLAY_METRICS_FIELDS.dpv.name)
    }
  },
  conversion: {
    purchaseRate: {
      shouldExportMultipleRequest: ['beacon-sales', 'beacon-traffic'],
      'beacon-sales': {
        indexName: 'beacon-sales',
        aggregationList: aggregationListGenerator('sales', ['unitsSold', 'retailSales', 'retailPrice']),
        derivedFieldsJson: `[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"beacon","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"beacon","indexName":"sales"}]`,
        sortByField: fieldToArrayFormatter('sales', 'unitsSold')
      },
      'beacon-traffic': {
        indexName: 'beacon-traffic',
        aggregationList: aggregationListGenerator('traffic', ['totalClicks', 'organicClicks']),
        sortByField: fieldToArrayFormatter('traffic', 'totalClicks'),
        derivedFieldsJson: `[{"name":"purchaseRate","displayName":"Conversion Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"traffic__totalClicks>0?sales__unitsSold/traffic__totalClicks:0","dependantFieldOverrides":{"primaryDependantFieldName":"unitsSold","overridesByFieldName":{"traffic__totalClicks":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"totalClicks","displayName":"Total Traffic","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","appName":"beacon","indexName":"traffic","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"traffic__totalClicks"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"IVAR","value":"traffic__totalClicks"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"beacon","indexName":"sales"}]`
      }
    }
  },
  contentV2: {
    indexName: 'beacon-contentChanged',
    aggregationList: [
      ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true],
      ['Matched Title Count', 'titleStats.matchedContentCount', 'sum', true],
      [
        'Title',
        'titleAccuracy',
        'computed',
        true,
        '((titleStats.approvedContentCount_sum > 0) ? ((titleStats.matchedContentCount_sum / titleStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Bullets Count', 'bulletsStats.approvedContentCount', 'sum', true],
      ['Matched Bullets Count', 'bulletsStats.matchedContentCount', 'sum', true],
      [
        'Bullets',
        'bulletsAccuracy',
        'computed',
        true,
        '((bulletsStats.approvedContentCount_sum > 0) ? ((bulletsStats.matchedContentCount_sum / bulletsStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Images Count', 'imageUrlsStats.approvedContentCount', 'sum', true],
      ['Matched Images Count', 'imageUrlsStats.matchedContentCount', 'sum', true],
      [
        'Images',
        'imageUrlsAccuracy',
        'computed',
        true,
        '((imageUrlsStats.approvedContentCount_sum > 0) ? ((imageUrlsStats.matchedContentCount_sum / imageUrlsStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Videos Count', 'videoUrlsStats.approvedContentCount', 'sum', true],
      ['Matched Videos Count', 'videoUrlsStats.matchedContentCount', 'sum', true],
      [
        'Videos',
        'videoUrlsAccuracy',
        'computed',
        true,
        '((videoUrlsStats.approvedContentCount_sum > 0) ? ((videoUrlsStats.matchedContentCount_sum / videoUrlsStats.approvedContentCount_sum)) : (1))'
      ]
    ],
    sortByField: ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true]
  },
  contentV3: {
    indexName: 'beacon-contentChanged',
    aggregationList: [
      ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true],
      ['Matched Title Count', 'titleStats.matchedContentCount', 'sum', true],
      [
        'Title',
        'titleAccuracy',
        'computed',
        true,
        '((titleStats.approvedContentCount_sum > 0) ? ((titleStats.matchedContentCount_sum / titleStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Bullets Count', 'bulletsStats.approvedContentCount', 'sum', true],
      ['Matched Bullets Count', 'bulletsStats.matchedContentCount', 'sum', true],
      [
        'Bullets',
        'bulletsAccuracy',
        'computed',
        true,
        '((bulletsStats.approvedContentCount_sum > 0) ? ((bulletsStats.matchedContentCount_sum / bulletsStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Images Count', 'imageUrlsStats.approvedContentCount', 'sum', true],
      ['Matched Images Count', 'imageUrlsStats.matchedContentCount', 'sum', true],
      [
        'Images',
        'imageUrlsAccuracy',
        'computed',
        true,
        '((imageUrlsStats.approvedContentCount_sum > 0) ? ((imageUrlsStats.matchedContentCount_sum / imageUrlsStats.approvedContentCount_sum)) : (1))'
      ],
      ['Approved Videos Count', 'videoUrlsStats.approvedContentCount', 'sum', true],
      ['Matched Videos Count', 'videoUrlsStats.matchedContentCount', 'sum', true],
      [
        'Videos',
        'videoUrlsAccuracy',
        'computed',
        true,
        '((videoUrlsStats.approvedContentCount_sum > 0) ? ((videoUrlsStats.matchedContentCount_sum / videoUrlsStats.approvedContentCount_sum)) : (1))'
      ]
    ],
    sortByField: ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true]
  },
  content: {
    contentScore: {
      indexName: 'beacon-content',
      aggregationList: aggregationListGenerator('content', [
        'contentScore',
        'titleScore',
        'bulletScore',
        'imageScore',
        'videoScore',
        'aplusScore'
      ]),
      sortByField: fieldToArrayFormatter('content', 'contentScore')
    },
    contentAccuracy: {
      indexName: 'beacon-contentChanged',
      aggregationList: [
        ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true],
        ['Matched Title Count', 'titleStats.matchedContentCount', 'sum', true],
        [
          'Title',
          'titleAccuracy',
          'computed',
          true,
          '((titleStats.approvedContentCount_sum > 0) ? ((titleStats.matchedContentCount_sum / titleStats.approvedContentCount_sum)) : (1))'
        ],
        ['Approved Bullets Count', 'bulletsStats.approvedContentCount', 'sum', true],
        ['Matched Bullets Count', 'bulletsStats.matchedContentCount', 'sum', true],
        [
          'Bullets',
          'bulletsAccuracy',
          'computed',
          true,
          '((bulletsStats.approvedContentCount_sum > 0) ? ((bulletsStats.matchedContentCount_sum / bulletsStats.approvedContentCount_sum)) : (1))'
        ],
        ['Approved Images Count', 'imageUrlsStats.approvedContentCount', 'sum', true],
        ['Matched Images Count', 'imageUrlsStats.matchedContentCount', 'sum', true],
        [
          'Images',
          'imageUrlsAccuracy',
          'computed',
          true,
          '((imageUrlsStats.approvedContentCount_sum > 0) ? ((imageUrlsStats.matchedContentCount_sum / imageUrlsStats.approvedContentCount_sum)) : (1))'
        ],
        ['Approved Videos Count', 'videoUrlsStats.approvedContentCount', 'sum', true],
        ['Matched Videos Count', 'videoUrlsStats.matchedContentCount', 'sum', true],
        [
          'Videos',
          'videoUrlsAccuracy',
          'computed',
          true,
          '((videoUrlsStats.approvedContentCount_sum > 0) ? ((videoUrlsStats.matchedContentCount_sum / videoUrlsStats.approvedContentCount_sum)) : (1))'
        ]
      ],
      sortByField: ['Approved Title Count', 'titleStats.approvedContentCount', 'sum', true]
    }
  },
  buybox: {
    winPercentage: {
      shouldExportMultipleRequest: ['beacon-buybox', 'beacon-sales'],
      'beacon-buybox': {
        indexName: 'beacon-buybox',
        aggregationList: [
          fieldToArrayFormatter('buybox', 'buyBoxWeighted'),
          fieldToArrayFormatter('buybox', 'winsByClient'),
          fieldToArrayFormatter('buybox', 'winPercentage'),
          fieldToArrayFormatter('buybox', 'winsBySeller'),
          fieldToArrayFormatter('buybox', 'lossPercentage')
        ],
        sortByField: fieldToArrayFormatter('buybox', 'buyBoxWeighted')
      },
      'beacon-sales': {
        indexName: 'beacon-sales',
        aggregationList: [fieldToArrayFormatter('sales', 'unitsSold')],
        sortByField: fieldToArrayFormatter('sales', 'unitsSold')
      }
    },
    sellers: {
      shouldExportMultipleRequest: ['beacon-buybox', 'beacon-sales'],
      'beacon-buybox': {
        indexName: 'beacon-buybox',
        aggregationList: [fieldToArrayFormatter('buybox', 'winsBySeller')],
        derivedFieldsJson: `[{"name":"sellerWinPercentage","displayName":"Buy Box - Seller Win Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"sales__unitsSold>0?winsBySeller/sales__unitsSold:0","timePeriodAggregationFunctionType":"simple","timePeriodAggregationFunction":"avg","dependantFieldOverrides":{"primaryDependantFieldName":"winsBySeller","overridesByFieldName":{"sales__unitsSold":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"sales__unitsSold>0?winsBySeller/sales__unitsSold:0","timePeriodAggregationFunctionType":"simple","timePeriodAggregationFunction":"avg","dependantFieldOverrides":{"primaryDependantFieldName":"winsBySeller","overridesByFieldName":{"sales__unitsSold":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"winsBySeller","displayName":"Buy Box - Seller","dataType":"INTEGER","metricType":"PERCENT","aggregationFunction":"sum","canBeExported":false,"appName":"beacon","indexName":"buybox","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"winsBySeller","displayName":"Buy Box - Seller","dataType":"INTEGER","metricType":"PERCENT","aggregationFunction":"sum","canBeExported":false,"appName":"beacon","indexName":"buybox","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"beacon","indexName":"buybox"}]`,
        sortByField: fieldToArrayFormatter('buybox', 'winsBySeller')
      },
      'beacon-sales': {
        indexName: 'beacon-sales',
        aggregationList: [fieldToArrayFormatter('sales', 'unitsSold')],
        sortByField: fieldToArrayFormatter('sales', 'unitsSold'),
        derivedFieldsJson: `[{"name":"sellerWinPercentage","displayName":"Buy Box - Seller Win Rate","dataType":"DECIMAL","metricType":"PERCENT","aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"sales__unitsSold>0?winsBySeller/sales__unitsSold:0","timePeriodAggregationFunctionType":"simple","timePeriodAggregationFunction":"avg","dependantFieldOverrides":{"primaryDependantFieldName":"winsBySeller","overridesByFieldName":{"sales__unitsSold":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}},"overrides":{"stacklineSku_Override":{"aggregationFunctionType":"derived","aggregationFunction":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"aggregationFunctionExpressionString":"sales__unitsSold>0?winsBySeller/sales__unitsSold:0","timePeriodAggregationFunctionType":"simple","timePeriodAggregationFunction":"avg","dependantFieldOverrides":{"primaryDependantFieldName":"winsBySeller","overridesByFieldName":{"sales__unitsSold":{"dataFetchStrategy":"chained","groupByFieldName":"stacklineSku","groupByResultType":"multivalue"}}}}},"dependentFields":[{"name":"unitsSold","displayName":"Units Sold","dataType":"INTEGER","metricType":"VOLUME","aggregationFunction":"sum","canBeExported":true,"appName":"beacon","indexName":"sales","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"chained"},{"name":"winsBySeller","displayName":"Buy Box - Seller","dataType":"INTEGER","metricType":"PERCENT","aggregationFunction":"sum","canBeExported":false,"appName":"beacon","indexName":"buybox","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"}],"primaryDependantField":{"name":"winsBySeller","displayName":"Buy Box - Seller","dataType":"INTEGER","metricType":"PERCENT","aggregationFunction":"sum","canBeExported":false,"appName":"beacon","indexName":"buybox","groupByFieldName":"stacklineSku","groupByResultType":"multivalue","dataFetchStrategy":"parallel"},"aggregationFunctionEvaluator":{"tokens":[{"type":"IVAR","value":"sales__unitsSold"},{"type":"INUMBER","value":0},{"type":"IOP2","value":">"},{"type":"IEXPR","value":[{"type":"IVAR","value":"winsBySeller"},{"type":"IVAR","value":"sales__unitsSold"},{"type":"IOP2","value":"/"}]},{"type":"IEXPR","value":[{"type":"INUMBER","value":0}]},{"type":"IOP3","value":"?"}],"parser":{"options":{},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{},"consts":{"E":2.718281828459045,"PI":3.141592653589793,"true":true,"false":false}},"unaryOps":{},"binaryOps":{},"ternaryOps":{},"functions":{}},"appName":"beacon","indexName":"buybox"}]`
      }
    }
  },
  // This is Beacon V1 - v2 will be handled by ProductGrid request builder
  reviews: {
    reviewTrends: {
      indexName: 'beacon-reviews',
      searchBy: 'child',
      shouldDoAggregationFalse: true,
      shouldOverrideConditionFilter: true,
      sortByField: ['TimeStamp', 'timestamp', null, null],
      sortByOnlyFieldNames: ['timestamp', 'stars'],
      isReturnDocument: true,
      aggregationList: null,
      shouldUserNullAggregation: true,
      exportExtraConditions: [
        { fieldName: 'reviewText', queryType: 'match', values: [''] },
        { fieldName: 'stars', values: ['1', '2', '3', '4', '5'] }
      ]
    },
    reviews: {
      indexName: 'beacon-reviews',
      searchBy: 'child',
      shouldDoAggregationFalse: true,
      shouldOverrideConditionFilter: true,
      sortByField: ['TimeStamp', 'timestamp', null, null],
      sortByOnlyFieldNames: ['timestamp', 'stars'],
      isReturnDocument: true,
      aggregationList: null,
      shouldUserNullAggregation: true,
      exportExtraConditions: [
        { fieldName: 'reviewText', queryType: 'match', values: [''] },
        { fieldName: 'stars', values: ['1', '2', '3', '4', '5'] }
      ]
    }
  },
  operations: {
    shortages: {
      indexName: 'beacon-sd-asinDetailMetrics',
      aggregationList: aggregationListGenerator('shortage-dispute', [
        'invoiceAmount',
        'updatedShortageAmountBeforeDispute',
        'disputeSubmittedAmount',
        'disputeWonAmount',
        'shortageRatio'
      ]),
      sortByField: fieldToArrayFormatter('shortage-dispute', 'invoiceAmount'),
      period: 'year'
    }
  },
  compliance: {
    keymetrics: {
      indexName: 'beacon-compliance-selleritem',
      aggregationList: aggregationListGenerator('compliance-keywordad', ['unauthorized']),
      sortByField: fieldToArrayFormatter('compliance-keywordad', 'unauthorized'),
      period: 'year'
    },
    keywordCompliance: {
      indexName: 'beacon-compliance-keywordaditem',
      aggregationList: aggregationListGenerator('compliance-keywordad', ['unauthorized']),
      sortByField: fieldToArrayFormatter('compliance-keywordad', 'unauthorized'),
      period: 'year'
    },
    sellerCompliance: {
      indexName: 'beacon-compliance-selleritem',
      aggregationList: aggregationListGenerator('compliance-keywordad', ['unauthorized']),
      sortByField: fieldToArrayFormatter('compliance-keywordad', 'unauthorized'),
      period: 'year'
    }
  },
  forecasts: {
    forecastSummary: {
      shouldExportMultipleRequest: [
        'beacon-traffic',
        'beacon-sales',
        'beacon-new-content-metrics',
        'beacon-buybox',
        'beacon-ratings-reviews-metrics'
      ],
      'beacon-traffic': {
        indexName: 'beacon-traffic',
        aggregationList: [
          ['Organic Traffic Historical', 'organicClicks', 'sum', true],
          ['Paid Traffic Historical', 'adClicks', 'sum', true],
          ['Total Traffic Historical', 'totalClicks', 'sum', true],
          ['Other Traffic Historical', 'otherClicks', 'sum', true],
          ['Ad Spend Historical', 'adSpend', 'sum', true],
          ['Ad Sales Historical', 'adRetailSales', 'sum', true]
        ],
        aggregationSortByField: ['Total Traffic Historical', 'totalClicks', 'sum', true],
        sortByField: ['Organic Traffic Historical', 'organicClicks', 'sum', true],
        period: 'year'
      },
      'beacon-sales': {
        indexName: 'beacon-sales',
        aggregationList: [
          ['Retail Sales Historical', 'retailSales', 'sum', true],
          ['Units Sold Historical', 'unitsSold', 'sum', true],
          ['Wholesale Sales Historical', 'wholesaleSales', 'sum', true],
          [
            'Retail Price Historical',
            'retailPrice',
            'computed',
            true,
            '((unitsSold_sum > 0) ? ((retailSales_sum / unitsSold_sum)) : (0))'
          ],
          ['Weighted In-Stock Rate Historical', 'inStockWeighted', 'sum', true],
          [
            'In-Stock Rate Historical',
            'instockRate',
            'computed',
            true,
            '((unitsSold_sum > 0) ? ((inStockWeighted_sum / unitsSold_sum)) : (0))'
          ],
          ['Units on Hand Historical', 'unitsOnHand', 'sum', true],
          ['Retailer Margin Historical', 'retailerGrossMargin', 'sum', true],
          ['Brand Margin Historical', 'brandGrossMargin', 'sum', true]
        ],

        sortByField: ['Retail Sales Historical', 'retailSales', 'sum', true]
      },
      'beacon-new-content-metrics': {
        indexName: 'beacon-new-content-metrics',
        aggregationList: [
          [
            'Content Score Historical',
            'contentScore',
            'computed',
            true,
            '((unitsSold_sum > 0) ? ((weightedContentScoreByUnits_sum / unitsSold_sum)) : (0))'
          ],
          ['Units Sold Historical', 'unitsSold', 'sum', true],
          ['Weighted Content Score Historical', 'weightedContentScoreByUnits', 'sum', true]
        ],
        sortByField: ['Content Score Historical', 'contentScore', 'avg', true],
        shouldUseSortByAggregationFieldNull: true
      },
      'beacon-buybox': {
        indexName: 'beacon-buybox',
        aggregationList: [
          ['Weighted Buy Box Rate', 'buyBoxWeighted', 'sum', false],
          ['Weighted Buy Box Wins By Units Historical', 'winsByClient', 'sum', true],
          [
            'Buy Box Rate Historical',
            'winPercentage',
            'computed',
            true,
            '((buyBoxWeighted_sum > 0) ? ((winsByClient_sum / buyBoxWeighted_sum)) : (0))'
          ],
          ['Buy Box - Seller Historical', 'winsBySeller', 'sum', false],
          [
            'Buy Box - Loss Historical',
            'lossPercentage',
            'computed',
            false,
            '((buyBoxWeighted_sum > 0) ? ((winsBySeller_sum / buyBoxWeighted_sum)) : (0))'
          ]
        ],
        aggregationSortByField: ['Weighted Buy Box  Rate', 'buyBoxWeighted', 'sum', false],
        sortByField: ['Weighted Buy Box  Rate Historical', 'buyBoxWeighted', 'sum', true]
      },
      'beacon-ratings-reviews-metrics': {
        indexName: 'beacon-ratings-reviews-metrics',
        aggregationList: [
          ['Rolling 52 Weighted Rating Historical', 'rolling52WeekWeightedRating', 'sum', true],
          ['Rolling 52 Week Reviews Historical', 'rolling52WeekReviews', 'sum', true],
          [
            'Review Rating Historical',
            'reviewRating',
            'computed',
            true,
            '((rolling52WeekReviews_sum > 0) ? ((rolling52WeekWeightedRating_sum / rolling52WeekReviews_sum)) : (0))'
          ],
          ['Number of New Reviews Historical', 'currentWeekNewReviews', 'sum', true]
        ],
        shouldUseSortByAggregationFieldNull: true,
        setSortDirectionAsNull: true
      }
    }
  }
});
